/**
 * @name FormTransferirSaldo
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para transferir saldo entre dependentes do mesmo responsável
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { SwapHoriz as TransferIcon } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, InputAdornment, InputLabel, OutlinedInput, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { currencyFormatterParser } from '../../../../../utils/handleChange';
import type { DependenteComSaldo } from '../useDependentesData';

interface FormTransferirSaldoProps {
     open: boolean;
     onClose: () => void;
     dependentes: DependenteComSaldo[];
     onTransferir: (dados: { idAlunoOrigem: number; idAlunoDestino: number; valor: number; observacoes?: string }) => Promise<boolean>;
}

/**
 * Modal para transferir saldo entre dependentes
 * Usa a function fn_transferir_entre_dependentes do Supabase
 */
const FormTransferirSaldo: React.FC<FormTransferirSaldoProps> = ({ open, onClose, dependentes, onTransferir }) => {
     // Estados do formulário
     const [dependenteOrigem, setDependenteOrigem] = useState<DependenteComSaldo | null>(null);
     const [dependenteDestino, setDependenteDestino] = useState<DependenteComSaldo | null>(null);
     // Valor formatado e numérico
     const [valor, setValor] = useState<string>('');
     const [valorNumerico, setValorNumerico] = useState<number>(0);
     const [observacoes, setObservacoes] = useState<string>('');
     const [loading, setLoading] = useState(false);

     // Dependentes disponíveis (com saldo para origem)
     const dependentesComSaldo = dependentes.filter((dep) => (dep.saldo_disponivel || 0) > 0);

     // Dependentes destino (todos exceto o selecionado como origem)
     const dependentesDestino = dependentes.filter((dep) => dep.id_aluno !== dependenteOrigem?.id_aluno);

     // Saldo máximo disponível para transferência
     const saldoMaximo = dependenteOrigem?.saldo_disponivel || 0;

     // Validações
     const isValorValido = valorNumerico > 0 && valorNumerico <= saldoMaximo;
     const isFormularioValido = dependenteOrigem && dependenteDestino && isValorValido;

     // Limpar formulário
     const limparFormulario = useCallback(() => {
          setDependenteOrigem(null);
          setDependenteDestino(null);
          setValor('');
          setValorNumerico(0);
          setObservacoes('');
          setLoading(false);
     }, []);

     // Fechar modal
     const handleClose = useCallback(() => {
          if (!loading) {
               limparFormulario();
               onClose();
          }
     }, [loading, limparFormulario, onClose]);

     // Submit do formulário
     const handleSubmit = useCallback(async () => {
          if (!isFormularioValido || !dependenteOrigem || !dependenteDestino) return;

          setLoading(true);
          try {
               // Garante que nunca será enviado mais de 29 caracteres
               const observacoesLimitada = observacoes.trim().slice(0, 29) || undefined;
               const sucesso = await onTransferir({
                    idAlunoOrigem: dependenteOrigem.id_aluno!,
                    idAlunoDestino: dependenteDestino.id_aluno!,
                    valor: valorNumerico,
                    observacoes: observacoesLimitada,
               });

               if (sucesso) {
                    handleClose();
               }
          } catch (error) {
               console.error('Erro ao transferir saldo:', error);
          } finally {
               setLoading(false);
          }
     }, [isFormularioValido, dependenteOrigem, dependenteDestino, valorNumerico, observacoes, onTransferir, handleClose]);

     // Ações rápidas de valor
     const valoresRapidos = [10, 20, 50];

     return (
          <Dialog
               open={open}
               onClose={handleClose}
               maxWidth='sm'
               fullWidth
               PaperProps={{ sx: { borderRadius: 2 } }}
          >
               <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <TransferIcon color='primary' />
                         <Typography variant='h6'>Transferir Saldo Entre Dependentes</Typography>
                    </Box>
                    <Typography
                         variant='body2'
                         color='text.secondary'
                         sx={{ mt: 0.5 }}
                    >
                         Transfira saldo de um dependente para outro
                    </Typography>
               </DialogTitle>

               <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={3}>
                         {/* Seleção do Dependente Origem */}
                         <FormControl fullWidth>
                              <Autocomplete
                                   value={dependenteOrigem}
                                   onChange={(_, newValue) => {
                                        setDependenteOrigem(newValue);
                                        setDependenteDestino(null); // Reset destino
                                   }}
                                   options={dependentesComSaldo}
                                   getOptionLabel={(option) => `${option.aluno} - R$ ${(option.saldo_disponivel || 0).toFixed(2)}`}
                                   renderInput={(params) => (
                                        <TextField
                                             {...params}
                                             label='Dependente (Origem)'
                                             placeholder='Selecione quem vai transferir'
                                        />
                                   )}
                                   disabled={loading}
                              />
                              {dependentesComSaldo.length === 0 && (
                                   <Typography
                                        variant='caption'
                                        color='warning.main'
                                        sx={{ mt: 0.5 }}
                                   >
                                        Nenhum dependente com saldo disponível
                                   </Typography>
                              )}
                         </FormControl>

                         {/* Seleção do Dependente Destino */}
                         <FormControl fullWidth>
                              <Autocomplete
                                   value={dependenteDestino}
                                   onChange={(_, newValue) => setDependenteDestino(newValue)}
                                   options={dependentesDestino}
                                   getOptionLabel={(option) => `${option.aluno} - R$ ${(option.saldo_disponivel || 0).toFixed(2)}`}
                                   renderInput={(params) => (
                                        <TextField
                                             {...params}
                                             label='Dependente (Destino)'
                                             placeholder='Selecione quem vai receber'
                                        />
                                   )}
                                   disabled={loading || !dependenteOrigem}
                              />
                         </FormControl>

                         <Divider />

                         {/* Valor da Transferência */}
                         <FormControl fullWidth>
                              <InputLabel htmlFor='valor-transferencia'>Valor da Transferência</InputLabel>
                              <OutlinedInput
                                   id='valor-transferencia'
                                   value={valor}
                                   onChange={(e) => {
                                        const { formatted, numeric } = currencyFormatterParser(e.target.value);
                                        setValor(formatted);
                                        setValorNumerico(numeric);
                                   }}
                                   startAdornment={<InputAdornment position='start'>R$</InputAdornment>}
                                   label='Valor da Transferência'
                                   inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        maxLength: 17,
                                   }}
                                   disabled={loading || !dependenteOrigem}
                                   error={valor !== '' && !isValorValido}
                              />
                              {dependenteOrigem && (
                                   <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ mt: 0.5 }}
                                   >
                                        Saldo disponível: R$ {saldoMaximo.toFixed(2)}
                                   </Typography>
                              )}
                         </FormControl>

                         {/* Ações Rápidas de Valor */}
                         {dependenteOrigem && saldoMaximo > 0 && (
                              <Box>
                                   <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ mb: 1, display: 'block' }}
                                   >
                                        Valores rápidos:
                                   </Typography>
                                   <Stack
                                        direction='row'
                                        spacing={1}
                                   >
                                        {valoresRapidos
                                             .filter((val) => val <= saldoMaximo)
                                             .map((valorRapido) => {
                                                  const { formatted, numeric } = currencyFormatterParser(valorRapido.toString());
                                                  return (
                                                       <Button
                                                            key={valorRapido}
                                                            variant='outlined'
                                                            size='small'
                                                            onClick={() => {
                                                                 setValor(formatted);
                                                                 setValorNumerico(numeric);
                                                            }}
                                                            disabled={loading}
                                                       >
                                                            {formatted}
                                                       </Button>
                                                  );
                                             })}
                                   </Stack>
                              </Box>
                         )}

                         {/* Alertas de Validação */}
                         {valor !== '' && !isValorValido && <Alert severity='error'>{valorNumerico <= 0 ? 'Valor deve ser maior que zero' : `Valor não pode ser maior que R$ ${saldoMaximo.toFixed(2)}`}</Alert>}

                         {/* Resumo da Transferência */}
                         {isFormularioValido && (
                              <Alert severity='info'>
                                   <Typography variant='body2'>
                                        <strong>Transferência:</strong> {valor} de <strong>{dependenteOrigem?.aluno}</strong> para <strong>{dependenteDestino?.aluno}</strong>
                                   </Typography>
                              </Alert>
                         )}
                    </Stack>
               </DialogContent>

               <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                         onClick={handleClose}
                         disabled={loading}
                    >
                         Cancelar
                    </Button>
                    <Button
                         onClick={handleSubmit}
                         variant='contained'
                         disabled={!isFormularioValido || loading}
                         startIcon={<TransferIcon />}
                    >
                         {loading ? 'Transferindo...' : 'Transferir Saldo'}
                    </Button>
               </DialogActions>
          </Dialog>
     );
};

export default FormTransferirSaldo;
